
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import dateFormat from 'dateformat';
import DataSyncWarning from '../components/DataSyncWarning.vue';
import CopyLinkButton from '@/components/CopyLinkButton.vue';
import { StoreModel } from '@/models/store';
import { EtlProcessModel } from '@/models/etl_process';
import { EventHandler } from '@/modules/events';
import { EventBus } from '@/main';
import FeatureLockedNotify from '@/components/FeatureLockedNotify.vue';
import ActionButton from '@/components/ActionButton.vue';
import { isValidSyncDate } from '@/utils/isValidSyncDate';

@Component({
  components: {
    DataSyncWarning,
    FeatureLockedNotify,
    ActionButton,
    CopyLinkButton,
  },
})
export default class HeaderTitle extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  @Prop({ default: () => [] })
  public timeRangeLimit!: string[];
  @Prop({ default: false })
  public isAthenaFinished!: boolean;
  @Prop({ default: false })
  public hiddenEtlStatus!: boolean;
  public triggerDataDialog: boolean = false;
  public isLoggedInFromHades: boolean = false;
  /**
   * True from the moment Force Sync is clicked until fresh data lands (or the
   * page's 30s x 10 re-poll gives up). Without it the icon fell straight back
   * to the green tick while the snackbar still read "Data is syncing", because
   * `needToSyncData` only measures how OLD the data is — a sync the merchant
   * just started is invisible to it.
   */
  public isForceSyncing: boolean = false;
  private forceSyncTimeout: number = 0;

  public async created() {
    this.isLoggedInFromHades = localStorage.getItem('loggedInFromHades') === 'true';
    if (this.needToSyncData) {
      await this.triggerData();
    }
  }

  /** What the icon and link colour actually represent: a sync is outstanding. */
  public get isSyncing(): boolean {
    return this.needToSyncData || this.isForceSyncing;
  }

  public get needToSyncData() {
    const now = Date.now();
    let lastTimeEtl = Date.parse(this.timeRangeLimit[1])
    let interval = this.timeRangeLimit[2];
    if (!this.timeRangeLimit[1]) {
      for (const etlStatus of this.store.etlStatuses.items) {
        if (etlStatus.resource === 'order_daily_sales_olap') {
          lastTimeEtl = Date.parse(etlStatus.lastUpdatedAt);
          interval = 'hourly'
        }
      }
    }
    if (isNaN(lastTimeEtl)) {
      return true;
    }
    if (interval === 'daily') {
      return now > lastTimeEtl + 24 * 60 * 60 * 1000 + 30 * 60 * 1000;
    } else {
      return now > lastTimeEtl + 90 * 60 * 1000;
    }
  }

  public async forceSync() {
    this.isForceSyncing = true;
    // Bounded by the same budget as ReportPage's re-poll (30s x 10), so a sync
    // that never lands stops claiming to be running instead of spinning forever.
    if (this.forceSyncTimeout) {
      clearTimeout(this.forceSyncTimeout);
    }
    this.forceSyncTimeout = window.setTimeout(() => this.clearForceSyncing(), 10 * 30 * 1000);
    await this.triggerData();
    EventBus.$emit(
      'show-snackbar',
      'Data is syncing. This process can take some time!',
    );
    this.$emit('sync-triggered');
  }

  public get dataSyncMessage() {
    const rawDate = this.timeRangeLimit[1];

    if (this.isAthenaFinished) {
      let preMsg = '';
      if (this.canForceTrigger) {
        const interval = this.timeRangeLimit[2] === 'hourly' ? 'Hourly' : 'Daily';
        preMsg += `${interval} synced report. `;
      }
      if (!isValidSyncDate(rawDate)) {
        return `${preMsg}Not yet synced`;
      }
      const lastUpdatedTimeStr: string = dateFormat(
        rawDate,
        'mmm d, yyyy HH:MM:ss',
        false
      );
      return `${preMsg}Last updated time: ${lastUpdatedTimeStr}`;
    }
    if (this.timeRangeLimit.length === 0 || this.timeRangeLimit[0] === this.timeRangeLimit[1]) {
      return 'Importing Data';
    }
    const startDate = this.timeRangeLimit[0];
    const endDate = this.timeRangeLimit[1];
    if (!isValidSyncDate(startDate) || !isValidSyncDate(endDate)) {
      return 'Importing Data';
    }
    const startUpdatedTimeStr: string = dateFormat(
      startDate,
      'mmm d, yyyy HH:MM:ss',
      false
    );
    const lastUpdatedTimeStr: string = dateFormat(
      endDate,
      'mmm d, yyyy HH:MM:ss',
      false
    );
    return 'The data importing is inprogress, completed from ' +
      startUpdatedTimeStr +
      ', to ' +
      lastUpdatedTimeStr;
  }

  public get isReportDataPage() {
    return [
      'customReport',
      'templateReportByType',
      'templateReport',
      'detailReportPage',
      'productExplore',
      'viewCardReport',
      'detailReportByName',
    ].includes(this.$route.name || '');
  }

  public get showStatus() {
    if (this.hiddenEtlStatus) {
      return false;
    }
    return !this.isAthenaFinished || this.isReportDataPage;
  }

  public formatDatetime(datetime: string, format: string = 'mmm d, yy') {
    return dateFormat(datetime, format, false);
  }

  public async triggerData() {
    const now = Date.now();
    let lastSyncDataTime;
    // @ts-ignore: Unreachable code error
    if (window.LAST_SYNC_DATA_TIME) {
      // @ts-ignore: Unreachable code error
      lastSyncDataTime = window.LAST_SYNC_DATA_TIME;
      if (now < lastSyncDataTime + 30 * 60 * 1000) {
        return false;
      }
    }

    const eventHandler = new EventHandler({
      store: this.store,
    });
    try {
      eventHandler.track('AUTO_TRIGGER_DATA');
    } catch (error: any) {
      // skipped
    }
    try {
      const etlProcess: EtlProcessModel = new EtlProcessModel();
      await etlProcess.trigger();
      // @ts-ignore: Unreachable code error
      window.LAST_SYNC_DATA_TIME = Date.now()
    } catch (error: any) {
      try {
        eventHandler.track('AUTO_TRIGGER_DATA_FAILED');
      } catch (error: any) {
        // skipped
      }
    }
    //this.triggerDataDialog = true;
  }

  public get canForceTrigger() {
    if (!this.isAthenaFinished) {
      return false;
    }
    if (!this.store.currentPackage.id) {
      return true;
    }
    for (const rule of this.store.currentPackage.rules.items) {
      if (rule.code === 'data_sync_schedule') {
        if (rule.value === 'hourly') {
          return true;
        }
      }
    }
    return false;
  }

  @Watch('timeRangeLimit')
  private onTimeRangeLimitChanged(next: string[], prev: string[]) {
    // Fresh data landed — the claim is no longer true, so stop making it.
    if (this.isForceSyncing && next && next[1] && next[1] !== (prev || [])[1]) {
      this.clearForceSyncing();
    }
  }

  private clearForceSyncing() {
    this.isForceSyncing = false;
    if (this.forceSyncTimeout) {
      clearTimeout(this.forceSyncTimeout);
      this.forceSyncTimeout = 0;
    }
  }

  private beforeDestroy() {
    this.clearForceSyncing();
  }
}
